// src/components/navbarLinks.js
import DashboardIcon from '@mui/icons-material/Dashboard';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import InventoryIcon from '@mui/icons-material/Inventory';
import CategoryIcon from '@mui/icons-material/Category';
import HistoryIcon from '@mui/icons-material/History';
import BusinessIcon from '@mui/icons-material/Business';
import SettingsIcon from '@mui/icons-material/Settings';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import GroupIcon from '@mui/icons-material/Group';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaymentsIcon from '@mui/icons-material/Payments';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import SecurityIcon from '@mui/icons-material/Security';
import {
    Storage as DatabaseIcon
} from '@mui/icons-material';
import { getUserDetails } from '../utils/helpers';

// Base links available to all users
const baseLinks = [
    {
        text: 'Dashboard',
        icon: <DashboardIcon />,
        path: '/dashboard',
        description: 'Overview & Analytics'
    },
    {
        text: 'Products',
        icon: <InventoryIcon />,
        description: 'Manage Products',
        children: [
            {
                text: 'Units',
                icon: <CategoryIcon />,
                path: '/units',
                description: 'Products units'
            },
            {
                text: 'All Products',
                icon: <Inventory2Icon />,
                path: '/products',
                description: 'View all products'
            },
            {
                text: 'Inventory',
                icon: <InventoryIcon />,
                path: '/inventory',
                description: 'Manage stock levels'
            },
            {
                text: 'Categories',
                icon: <CategoryIcon />,
                path: '/categories',
                description: 'Product categories'
            },
            {
                text: 'Suppliers',
                icon: <LocalShippingIcon />,
                path: '/suppliers',
                description: 'Supplier management'
            }
        ]
    },
    {
        text: 'Orders',
        icon: <ShoppingCartIcon />,
        description: 'Order Management',
        children: [
            {
                text: 'POS',
                icon: <PointOfSaleIcon />,
                path: '/pos',
                description: 'Point of Sale'
            },
            {
                text: 'All Orders',
                icon: <ReceiptLongIcon />,
                path: '/history',
                description: 'View all orders'
            },
        ]
    },
    {
        text: 'Loans',
        icon: <PaymentsIcon />,
        description: 'Loans Management',
        children: [
            {
                text: 'Store',
                icon: <AccountBalanceIcon />,
                path: '/store-loans',
                description: 'Store financing'
            },
            {
                text: 'Customer',
                icon: <MonetizationOnIcon />,
                path: '/customer-loans',
                description: 'Customer credit'
            }
        ]
    },
    {
        text: 'Payments',
        icon: <PaymentsIcon />,
        description: 'Payment Management',
        children: [
            {
                text: 'Store',
                icon: <AccountBalanceIcon />,
                path: '/store-loans-payments',
                description: 'Store Payments'
            },
            {
                text: 'Customers',
                icon: <AccountBalanceIcon />,
                path: '/customer-loans-payments',
                description: 'Customer Payments'
            }
        ]
    },
    {
        text: 'Customers',
        icon: <GroupIcon />,
        description: 'Customer Management',
        children: [
            {
                text: 'Customers',
                icon: <GroupIcon />,
                path: '/customer',
                description: 'Customer management'
            }
        ]
    },
    {
        text: 'Expenses',
        icon: <CreditCardIcon />,
        description: 'Expenses Management',
        children: [
            {
                text: 'Expenses',
                icon: <CreditCardIcon />,
                path: '/expense',
                description: 'Expenses tracking'
            },
        ]
    },
];

// Settings section - only for admin users
const settingsLinks = [
    {
        text: 'Settings',
        icon: <SettingsIcon />,
        description: 'System Configuration',
        children: [
            {
                text: 'Company',
                icon: <BusinessIcon />,
                path: '/company',
                description: 'Company settings'
            },
            {
                text: 'User Management',
                icon: <ManageAccountsIcon />,
                path: '/users',
                description: 'Manage users'
            },
            {
                text: 'Permissions',
                icon: <SecurityIcon />,
                path: '/permission',
                description: 'Manage permissions'
            },
            {
                text: 'System Logs',
                icon: <HistoryIcon />,
                path: '/system-logs',
                description: 'View system activity logs'
            },
            {
                text: 'Database',
                icon: <DatabaseIcon />,
                path: '/database',
                description: 'Manage database & cache'
            }
        ]
    }
];

// Get all navbar links
export const getNavbarLinks = () => {
    const userRole = getUserDetails('roleId');
    if (userRole === 'AD') {
        return [...baseLinks, ...settingsLinks];
    }
    return baseLinks;
};

// Get navbar links with all children flattened
const getAllLinks = () => {
    const links = getNavbarLinks();
    const allLinks = [];
    
    links.forEach(link => {
        if (link.children) {
            link.children.forEach(child => {
                allLinks.push({
                    parent: link.text,
                    parentIcon: link.icon,
                    parentDescription: link.description,
                    ...child
                });
            });
        } else {
            allLinks.push({
                parent: null,
                parentIcon: null,
                parentDescription: null,
                ...link
            });
        }
    });
    
    return allLinks;
};

// Find current location based on pathname
export const getCurrentLocation = (pathname) => {
    const allLinks = getAllLinks();
    
    // Find the matching link
    const matchingLink = allLinks.find(link => link.path === pathname);
    
    if (matchingLink) {
        return {
            main: matchingLink.parent || matchingLink.text,
            mainIcon: matchingLink.parentIcon || matchingLink.icon,
            sub: matchingLink.parent ? matchingLink.text : null,
            subIcon: matchingLink.parent ? matchingLink.icon : null,
            description: matchingLink.description,
            parentDescription: matchingLink.parentDescription
        };
    }
    
    // If no exact match, try to find a parent match
    const parentMatch = allLinks.find(link => {
        if (link.path) {
            return pathname.startsWith(link.path) && link.path !== '/';
        }
        return false;
    });
    
    if (parentMatch) {
        return {
            main: parentMatch.parent || parentMatch.text,
            mainIcon: parentMatch.parentIcon || parentMatch.icon,
            sub: parentMatch.parent ? parentMatch.text : null,
            subIcon: parentMatch.parent ? parentMatch.icon : null,
            description: parentMatch.description,
            parentDescription: parentMatch.parentDescription
        };
    }
    
    // Default fallback
    return {
        main: 'Dashboard',
        mainIcon: <DashboardIcon />,
        sub: null,
        subIcon: null,
        description: 'Overview & Analytics',
        parentDescription: null
    };
};

export const navbarLinks = getNavbarLinks();